import { useNavigation } from "@react-navigation/native";
import * as Sentry from "@sentry/react-native";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { observer } from "mobx-react-lite";
import { FormattedMessage } from "react-intl";
import { Linking, Platform, ScrollView, StyleSheet } from "react-native";
import Permissions, {
  RESULTS,
  requestMultiple,
} from "react-native-permissions";
import { Button } from "~/components/Button";
import { Icon } from "~/components/Icon";
import { Pressable } from "~/components/Pressable";
import { Spacer } from "~/components/Spacer";
import { Spinner } from "~/components/Spinner";
import { Text } from "~/components/Text";
import { View } from "~/components/View";
import { styleConstants } from "~/style/styleConstants";
import { waitForUserToReturnToApp } from "~/utils/waitForUserToReturnToApp";
import { useErrorAlert } from "../error-alert";
import { PermissionSwitch } from "./PermissionSwitch";

export const RequestCameraPermissionView = observer(
  function RequestCameraPermissionView({
    children,
    showBackButton = true,
    isOnVideoChat = false,
  }: {
    children: React.ReactNode | React.ReactNode[];
    showBackButton?: boolean;
    isOnVideoChat?: boolean;
  }) {
    const navigation = useNavigation();
    const client = useQueryClient();
    const errorAlert = useErrorAlert();

    const query = useQuery({
      queryKey: ["camera-permission"],
      async queryFn() {
        const cameraPermission =
          Platform.OS === "android"
            ? Permissions.PERMISSIONS.ANDROID.CAMERA
            : Permissions.PERMISSIONS.IOS.CAMERA;
        const microphonePermission =
          Platform.OS === "android"
            ? Permissions.PERMISSIONS.ANDROID.RECORD_AUDIO
            : Permissions.PERMISSIONS.IOS.MICROPHONE;
        const permission = [cameraPermission, microphonePermission];

        const result = await Permissions.checkMultiple(permission);
        const cam = result[cameraPermission];
        const mic = result[microphonePermission];

        return cam === "blocked" || mic === "blocked"
          ? "blocked"
          : cam === "unavailable" || mic === "unavailable"
            ? "unavailable"
            : cam === "limited" || mic === "limited"
              ? "limited"
              : cam === "denied" || mic === "denied"
                ? "denied"
                : "granted";
      },
    });

    if (query.data === RESULTS.GRANTED) {
      return <>{children}</>;
    }

    return (
      <ScrollView bounces={false} contentContainerStyle={style.container}>
        <View
          flex
          centerContent
          paddingHorizontalExtraLarge
          paddingVerticalLarge
        >
          <Icon
            size={125}
            name="camera-large"
            color={styleConstants.colorTextTheme}
          />

          <Spacer extraLarge />
          <Spacer />

          <PermissionSwitch
            status={query.data}
            denied={
              <>
                <Text alignCenter sizeExtraLarge weightExtraBold colorDark>
                  <FormattedMessage
                    id="iVQJ77"
                    defaultMessage="Allow camera access"
                    description="Button on RecordVideoScreen that lets user enable camera and microphone permission"
                  />
                </Text>
                <Spacer large />
                <Text alignCenter colorDark>
                  {isOnVideoChat ? (
                    <FormattedMessage
                      id="POBxBZ"
                      defaultMessage="We need access to your camera to enable live video chat."
                      description="Subtitle on VideoChatScreen that explains why we need users camera permission"
                    />
                  ) : (
                    <FormattedMessage
                      id="ZRecAa"
                      defaultMessage="We need access to your camera to record your video profile."
                      description="Subtitle on RecordVideoScreen that explains why we need users camera permission"
                    />
                  )}
                </Text>
              </>
            }
            unavailable={
              <>
                <Text alignCenter sizeExtraLarge weightExtraBold colorDark>
                  <FormattedMessage
                    id="1eQ9d3"
                    defaultMessage="Camera unavailable"
                    description="Title of RecordVideoScreen if camera is not available"
                  />
                </Text>
                <Spacer large />
                <Text alignCenter colorDark>
                  <FormattedMessage
                    id="yUJY7l"
                    defaultMessage="Please enable camera permission on device and try again."
                    description="Subtitle on RecordVideoScreen if camera is not available"
                  />
                </Text>
              </>
            }
            blocked={
              <>
                <Text alignCenter sizeExtraLarge weightExtraBold colorDark>
                  <FormattedMessage
                    id="BQzdVK"
                    defaultMessage="Camera Blocked"
                    description="Title of RecordVideoScreen if camera permission is blocked"
                  />
                </Text>
                <Spacer large />
                <Text alignCenter colorDark>
                  <FormattedMessage
                    id="jWHMam"
                    defaultMessage="Camera permission is blocked. Please tap the <bold>Open Settings</bold> button below and enable <bold>camera</bold> and <bold>microphone</bold> permission in your device settings."
                    description="Subtitle on RecordVideoScreen if camera permission is blocked and user has to manually enable it in settings of his device"
                    values={{
                      bold: (chunks: React.ReactNode) => (
                        <Text weightExtraBold>{chunks}</Text>
                      ),
                    }}
                  />
                </Text>
              </>
            }
            limited={{ goto: "blocked" }}
            granted={undefined}
            unknown={undefined}
          />

          <Spacer extraLarge />

          <View paddingHorizontalExtraLarge paddingVerticalLarge>
            {!query.isSuccess ? (
              <Spinner />
            ) : (
              <PermissionSwitch
                status={query.data}
                denied={
                  <Button
                    large
                    title={intl.formatMessage({
                      id: "ETT44m",
                      defaultMessage: "Continue",
                      description:
                        "Button on RecordVideoScreen that lets user enable camera permission",
                    })}
                    onPress={async function onContinuePress() {
                      try {
                        const cameraPermission =
                          Platform.OS === "android"
                            ? Permissions.PERMISSIONS.ANDROID.CAMERA
                            : Permissions.PERMISSIONS.IOS.CAMERA;
                        const microphonePermission =
                          Platform.OS === "android"
                            ? Permissions.PERMISSIONS.ANDROID.RECORD_AUDIO
                            : Permissions.PERMISSIONS.IOS.MICROPHONE;
                        const permission = [
                          cameraPermission,
                          microphonePermission,
                        ];

                        const result = await requestMultiple(permission);
                        const cam = result[cameraPermission];
                        const mic = result[microphonePermission];

                        const state =
                          cam === "blocked" || mic === "blocked"
                            ? "blocked"
                            : cam === "unavailable" || mic === "unavailable"
                              ? "unavailable"
                              : cam === "limited" || mic === "limited"
                                ? "limited"
                                : cam === "denied" || mic === "denied"
                                  ? "denied"
                                  : "granted";

                        // This is here because checking permissions sometimes doesnt work as expected
                        client.setQueryData(["camera-permission"], state);
                      } catch (error) {
                        errorAlert(error);
                        Sentry.captureException(error);
                      }
                    }}
                  />
                }
                unavailable={
                  <Button
                    large
                    disabled={!query.isSuccess}
                    title={intl.formatMessage({
                      id: "6Fi83M",
                      defaultMessage: "Unavailable",
                      description:
                        "Button on RecordVideoScreen that lets user know that camera is not available",
                    })}
                  />
                }
                blocked={
                  <Button
                    large
                    disabled={!query.isSuccess}
                    title={intl.formatMessage({
                      id: "VpkyeR",
                      defaultMessage: "Open settings",
                      description:
                        "Button on RecordVideoScreen that lets user open settings of his device",
                    })}
                    onPress={async () => {
                      await Linking.openSettings();
                      await waitForUserToReturnToApp();
                      await client.invalidateQueries({
                        queryKey: ["camera-permission"],
                      });
                    }}
                  />
                }
                limited={{ goto: "blocked" }}
                // Should never happen since we show a spinner until we have a result
                unknown={{ goto: "denied" }}
                // Granted case is handled at the top of this component
                granted={undefined}
              />
            )}
          </View>

          {showBackButton && (
            <Pressable
              onPress={() => {
                navigation.goBack();
              }}
              centerContent
              style={({ pressed }) => [
                style.backButton,
                {
                  opacity: pressed ? 0.5 : 1,
                },
              ]}
            >
              <Icon
                name="arrow-back"
                size={24}
                color={styleConstants.colorTextDark}
              />
            </Pressable>
          )}
        </View>
      </ScrollView>
    );
  },
);

const style = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: styleConstants.colorBackgroundWhite,
  },
  backButton: {
    position: "absolute",
    top: 0,
    left: 0,
    borderRadius: 24,
    width: 48,
    height: 48,
  },
});
